import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, FileCheck, ArrowRight, ArrowLeft, Upload, X } from "lucide-react";
import { toast } from "sonner";

const ALLOWED_EXTS = [".jpg", ".jpeg", ".png", ".webp", ".pdf", ".heic"];
const MAX_BYTES = 8 * 1024 * 1024;

export default function CoachApply() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    certification: "", cert_body: "", cert_expiry: "",
    insurance_provider: "", insurance_policy_number: "", insurance_expiry: "",
    bio: "", years_experience: 1,
  });
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateFile = (f) => {
    if (!f) return "Please select a file";
    const dot = f.name.lastIndexOf(".");
    const ext = dot >= 0 ? f.name.slice(dot).toLowerCase() : "";
    if (!ALLOWED_EXTS.includes(ext)) return `Unsupported file type (${ext || "no extension"}). Allowed: ${ALLOWED_EXTS.join(", ")}`;
    if (f.size > MAX_BYTES) return "File too large (max 8MB)";
    return null;
  };

  const pickFile = (which) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateFile(f);
    if (err) { toast.error(err); e.target.value = ""; return; }
    if (which === "insurance") setInsuranceFile(f);
    else setCertFile(f);
  };

  const uploadDoc = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/coach/documents?kind=${kind}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!insuranceFile) { toast.error("Insurance document is required"); return; }
    if (!certFile) { toast.error("Certification document is required"); return; }
    setSaving(true);
    try {
      // 1. Upload both proof documents first — backend enforces both must exist before /coach/apply accepts
      await Promise.all([
        uploadDoc("insurance", insuranceFile),
        uploadDoc("certification", certFile),
      ]);
      // 2. Submit application metadata
      await api.post("/coach/apply", {
        ...form,
        years_experience: Number(form.years_experience),
      });
      if (user) setUser({ ...user, coach_status: "pending" });
      toast.success("Application submitted for admin review");
      nav("/coach/pending");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg flex items-center justify-center p-6 py-12">
      <div className="w-full max-w-2xl">
        <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>
        <div className="label-mono mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#CCFF00]" /> COACH APPLICATION
        </div>
        <h1 className="h-display text-4xl md:text-5xl mb-4 tracking-tighter">Verify to coach on Rally Lab.</h1>
        <p className="text-[#A3A3A3] mb-8 max-w-xl">
          We hold every coach to the same bar: valid coaching certification plus active public-liability insurance. An admin reviews each application within 48h. You can&apos;t publish offerings or access player data until approved.
        </p>

        <form onSubmit={submit} className="card-surface p-6 space-y-5" data-testid="coach-apply-form">
          <div className="label-mono mb-2 text-[#CCFF00]">CERTIFICATION</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Certification title" required value={form.certification} onChange={(v) => update("certification", v)}
              placeholder="e.g. England Squash Level 2" testid="cert-title" />
            <Field label="Awarding body" value={form.cert_body} onChange={(v) => update("cert_body", v)}
              placeholder="e.g. England Squash" testid="cert-body" />
          </div>
          <Field label="Certification expiry (if applicable)" type="date" value={form.cert_expiry} onChange={(v) => update("cert_expiry", v)} testid="cert-expiry" />
          <FileField
            label="Upload certification (JPG / PNG / PDF, max 8MB)"
            required
            file={certFile}
            onPick={pickFile("certification")}
            onClear={() => setCertFile(null)}
            testid="cert-file"
            accent="#CCFF00"
          />

          <div className="label-mono mb-2 mt-2 text-[#FF4500]">INSURANCE</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Insurance provider" required value={form.insurance_provider} onChange={(v) => update("insurance_provider", v)}
              placeholder="e.g. Sportscover" testid="ins-provider" />
            <Field label="Policy number" required value={form.insurance_policy_number} onChange={(v) => update("insurance_policy_number", v)}
              testid="ins-policy" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Policy expiry" required type="date" value={form.insurance_expiry} onChange={(v) => update("insurance_expiry", v)} testid="ins-expiry" />
            <Field label="Years experience" type="number" value={form.years_experience} onChange={(v) => update("years_experience", v)} testid="years" />
          </div>
          <FileField
            label="Upload insurance certificate (JPG / PNG / PDF, max 8MB)"
            required
            file={insuranceFile}
            onPick={pickFile("insurance")}
            onClear={() => setInsuranceFile(null)}
            testid="ins-file"
            accent="#FF4500"
          />

          <div className="label-mono mb-2 mt-2">COACH BIO</div>
          <Field label="Short bio (visible to players)" textarea value={form.bio} onChange={(v) => update("bio", v)}
            placeholder="A few sentences about your background, achievements, and coaching style." testid="bio" />

          <div className="text-xs text-[#A3A3A3] border border-[#262626] p-3 flex items-start gap-2">
            <FileCheck className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
            By submitting I confirm the above details are accurate, my insurance is current, and the uploaded documents are genuine. Rally Lab may request additional supporting documents.
          </div>

          <button type="submit" disabled={saving} className="btn-volt w-full py-4 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="submit-application">
            {saving ? "Submitting…" : "Submit application"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, textarea, testid }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}{required && <span className="text-[#FF4500]"> *</span>}</div>
      {textarea ? (
        <textarea value={value || ""} rows={3} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
      ) : (
        <input type={type} value={value ?? ""} required={required} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
      )}
    </div>
  );
}

function FileField({ label, file, onPick, onClear, required, testid, accent = "#CCFF00" }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}{required && <span className="text-[#FF4500]"> *</span>}</div>
      {file ? (
        <div className="flex items-center justify-between border border-[#262626] bg-[#141414] px-3 py-2.5 text-sm" data-testid={`${testid}-selected`}>
          <div className="flex items-center gap-2 truncate">
            <FileCheck className="w-4 h-4 shrink-0" style={{ color: accent }} />
            <span className="truncate">{file.name}</span>
            <span className="text-[#A3A3A3] text-xs shrink-0">· {(file.size / 1024).toFixed(0)} KB</span>
          </div>
          <button type="button" onClick={onClear} data-testid={`${testid}-clear`} className="text-[#A3A3A3] hover:text-[#FF4500] shrink-0 ml-2">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label className="flex items-center gap-2 border border-dashed border-[#262626] hover:border-[color:var(--accent)] cursor-pointer px-3 py-3 text-sm text-[#A3A3A3]"
               style={{ "--accent": accent }}>
          <Upload className="w-4 h-4" style={{ color: accent }} />
          <span>Click to choose a file</span>
          <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.heic" onChange={onPick} className="hidden" data-testid={testid} />
        </label>
      )}
    </div>
  );
}
